import type { AiProposalEngine } from "@/ai/engines/engine.types";
import { firstSentence, groupId, groupLabel, groupMidpointSec, inferProposalTimeSec, proposalHasKeyword } from "@/ai/engines/engineUtils";

const CONTINUITY_KEYWORDS = ["continuity", "match", "spatial", "axis", "eyeline", "drift"];

export const continuityEngine: AiProposalEngine = (context) => {
  const drafts = context.proposals
    .filter((proposal) => proposalHasKeyword(proposal, CONTINUITY_KEYWORDS))
    .map((proposal) => {
      const summary = firstSentence(proposal.rationale, "Continuity review suggested for this beat.");
      const timeSec = inferProposalTimeSec(context, proposal);

      return {
        type: "continuity" as const,
        subtype: summary.toLowerCase().includes("spatial") || summary.toLowerCase().includes("axis") ? "spatial_break" as const : "continuity_risk" as const,
        severity: (proposal.confidence ?? 0) >= 0.75 ? "warning" as const : "suggestion" as const,
        summary,
        details: {
          riskFactor: summary,
          futureNodesAffected: [proposal.id],
          recommendedLock: proposal.confidence && proposal.confidence >= 0.75 ? "lock_before_publish" : "human_review",
        },
        proposedAction: "review" as const,
        nodeId: proposal.id,
        timeSec,
        frame: Math.floor(timeSec * context.fps),
        action: "Review continuity before publish",
      };
    });

  for (const [index, group] of context.groups.entries()) {
    const label = groupLabel(group);
    if (!label || !CONTINUITY_KEYWORDS.some((keyword) => label.toLowerCase().includes(keyword))) continue;

    const timeSec = groupMidpointSec(group, context.anchorTimeSec);
    drafts.push({
      type: "continuity",
      subtype: "continuity_risk",
      severity: "suggestion",
      summary: `${label} needs a continuity check.`,
      details: {
        riskFactor: label,
        futureNodesAffected: [],
        recommendedLock: "review_group_alignment",
      },
      proposedAction: "review",
      nodeId: `group:${groupId(group, `continuity-${index}`)}`,
      timeSec,
      frame: Math.floor(timeSec * context.fps),
      action: "Review continuity across proposal group",
    });
  }

  return drafts;
};